export interface SelectPlanItem {
  planId: number;
  titleKey: string;
  descriptionKey: string;
  priceKey?: string | null;
  priceRequestQuoteKey?: string | null;
  buttonKey: string;
  featuresKey: string;
  includesLabelKey?: string | null;
  recommended: boolean;
}

/** Plan IDs alignés avec la table Plan du backend (1=TRIAL, 2=BASIC, 3=STANDARD, 4=ENTERPRISE) */
export const PLAN_IDS = {
  BASIC: 2,
  STANDARD: 3,
  ENTERPRISE: 4,
} as const;

/** Configuration par défaut des plans pour la sélection (clés i18n sous selectPlan.*) */
export const DEFAULT_SELECT_PLAN_ITEMS: SelectPlanItem[] = [
  {
    planId: PLAN_IDS.BASIC,
    titleKey: 'selectPlan.basic.title',
    descriptionKey: 'selectPlan.basic.description',
    priceKey: 'selectPlan.basic.price',
    priceRequestQuoteKey: null,
    buttonKey: 'selectPlan.basic.button',
    featuresKey: 'selectPlan.basic.features',
    includesLabelKey: null,
    recommended: false,
  },
  {
    planId: PLAN_IDS.STANDARD,
    titleKey: 'selectPlan.standard.title',
    descriptionKey: 'selectPlan.standard.description',
    priceKey: 'selectPlan.standard.price',
    priceRequestQuoteKey: null,
    buttonKey: 'selectPlan.standard.button',
    featuresKey: 'selectPlan.standard.features',
    includesLabelKey: 'selectPlan.includesBasic',
    recommended: true,
  },
  {
    planId: PLAN_IDS.ENTERPRISE,
    titleKey: 'selectPlan.enterprise.title',
    descriptionKey: 'selectPlan.enterprise.description',
    priceKey: null,
    priceRequestQuoteKey: 'selectPlan.enterprise.priceRequestQuote',
    buttonKey: 'selectPlan.enterprise.button',
    featuresKey: 'selectPlan.enterprise.features',
    includesLabelKey: 'selectPlan.includesStandard',
    recommended: false,
  },
];

export interface SelectPlanProps {
  /** Company for which the plan is selected. If null/undefined, noCompanyRedirectTo is used or nothing is rendered. */
  companyId: number | null | undefined;
  /** Called when user selects a plan. App should create subscription and then call onSuccessRedirect if needed. */
  onSelectPlan: (params: {
    companyId: number;
    planId: number;
    isTrial?: boolean;
    autoRenew?: boolean;
  }) => void;
  isCreating?: boolean;
  createError?: import('../../services/axios/error.type').AppErrorResponse | null;
  /** List of plans to display (planId, i18n keys) */
  plans: SelectPlanItem[];
  /** i18n key prefix, e.g. 'selectPlan' for title -> 'selectPlan.title' */
  translationKeyPrefix?: string;
  /** Path to redirect when companyId is missing (e.g. '/companies') */
  noCompanyRedirectTo?: string;
  /** Called when companyId is missing; if not provided, redirect via navigate(noCompanyRedirectTo) */
  onNoCompany?: () => void;
  /** When true, beforeunload/popstate prevent leaving until plan is selected. Set subscriptionCreatedSuccess to true when creation succeeds. */
  blockLeaveUntilComplete?: boolean;
  /** Set to true when subscription was created successfully (releases leave prevention) */
  subscriptionCreatedSuccess?: boolean;
  /** If provided, called after releasing leave prevention (e.g. full-page redirect). Use this instead of redirecting in onSuccess to avoid the "leave page?" popup. */
  onSuccessRedirect?: () => void;
  /** When set, the plan with this planId is the current active plan: its button is disabled and shows currentPlanButtonKey. User can still select another plan to create a new subscription. */
  activePlanId?: number | null;
  /** i18n key for the disabled button when the plan is the current one (e.g. 'selectPlan.currentPlan'). */
  currentPlanButtonKey?: string;
}
